import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { useNavigate, useOutletContext } from "react-router-dom";
import { PageTitleBar } from "../../../../../components/PageTitleBar/PageTitleBar";
import { alpha, Button, Menu, MenuItem, MenuProps, Stack } from "@mui/material";
import styled from "@emotion/styled";
import { useDataTableV2Pagination } from "../../../../../components/TableV2/hooks/useDataTableV2Pagination";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DataTableV2 } from "../../../../../components/TableV2/DataTableV2";
import { useTableV2State } from "../../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { ControlledCustomSelect } from "../../../../../components/formsComponents";
import {
  downloadAutomobileAlbertaPDFApplications,
  downloadAutomobilePDFApplications,
  downloadAutomobilePDFApplicationsV2,
  fetchAutomobileAllApplicationsListAsync,
  previewAutomobileAlbertaPDFApplications,
  previewAutomobilePDFApplications,
  previewAutomobilePDFApplicationsV2,
} from "../../../../../redux/automobileApplication/automobileApplicationActions";
import {
  IAutomobileApplicationList,
  IAutomobileQuickAplication,
  INITIAL_QUICK_APPLICATION,
} from "../../../../../redux/automobileApplication/automobileApplication.types";
import { StandardTableActions } from "../../../../../components/StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { formatText } from "../../../../../helpers";
import { HistoryEdu, Preview, RequestQuote } from "@mui/icons-material";
import { QuickApplicationDialog } from "./AutomobileApplicationForm/QuickApplication";
import { COMMON_STATUS } from "../../../../../constants/constants";
import { PageContainer } from "../../../../../components/container/PageContainer";
import { ApplicationG4SignDialog } from "./ApplicationG4Sign/ApplicationG4SignDialog";
import { PolicyQuotesRightPanel } from "../PoliciesCommon/PolicyQuotesPanel/PolicyQuotesRightPanel";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }: any) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const APPLICATION_TYPES = [
  { label: "All Applications", value: "all_application" },
  { label: "Automobile (AB)", value: "automobile_ab" },
  { label: "Automobile (ON)", value: "automobile_on" },
];

const DOWNLOAD_FOR = ["automobile_ab", "automobile_on"];
const PREVIEW_FOR = ["automobile_ab", "automobile_on"];

export const AutomobileApplicationRoot = () => {
  const data = useOutletContext() as any;
  const customerId = data[0];
  const customerPolicyId = data[1];
  // const { addBreadcrumb } = useBreadcrumbContext();
  // const { getParm } = useQueryParms();
  // const tabValue = getParm("tab");
  // const selectedTab = tabValue ? Number(tabValue) : 0;

  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) =>
      storeState.automobile.allApplications.applicationsList,
  );

  const { customerInfo } = useSelector(
    (storeState: IStoreState) => storeState.automobile.basicDetails,
  );

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const { tablePagination, onPageChange, onRowsPerPageChange } =
    useDataTableV2Pagination({
      pageNumber: 1,
      rowsInPerPage: 25,
      extraFetchFactor: 1,
    });
  const [policyFormRoot, setPolicyFormRoot] = React.useState("all_application");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openPolicyCancel, setOpenPolicyCancel] =
    React.useState<boolean>(false);
  const [openFile, setOpenFile] = React.useState(false);
  const [openView, setOpenView] =
    React.useState<IAutomobileQuickAplication | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [openQuotes, setOpenQuotes] = React.useState(false);
  const [g4SignApp, setG4SignApp] = React.useState<IAutomobileApplicationList | null>(null);
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE",
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // const handleDownloadPreview =
  //   (incemental_number: number, type: "download" | "preview") => () => {
  //     if (type === "download") {
  //       dispatch(
  //         downloadAutomobilePDFApplications(customerPolicyId, incemental_number)
  //       );
  //     } else {
  //       dispatch(
  //         previewAutomobilePDFApplications(customerPolicyId, incemental_number)
  //       );
  //     }
  //   };

  const handleDownloadClick = (
    policyFormType: string,
    incremental_number: number,
    policy_period_effective_date?: string | null,
  ) => {
    const useV2 =
      policyFormType === "automobile_on" &&
      policy_period_effective_date &&
      policy_period_effective_date >= "2026-07-01";

    switch (policyFormType) {
      case "automobile_on": {
        if (useV2) {
          dispatch(
            downloadAutomobilePDFApplicationsV2(
              customerPolicyId,
              incremental_number,
              () => {
                setDownloading(false);
              },
            ),
          );
        } else {
          dispatch(
            downloadAutomobilePDFApplications(
              customerPolicyId,
              incremental_number,
              () => {
                setDownloading(false);
              },
            ),
          );
        }
        return;
      }
      case "automobile_ab": {
        dispatch(
          downloadAutomobileAlbertaPDFApplications(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            },
          ),
        );
        return;
      }
    }
  };

  const handlePreviewClick = (
    policyFormType: string,
    incremental_number: number,
    policy_period_effective_date?: string | null,
  ) => {
    const useV2 =
      policyFormType === "automobile_on" &&
      policy_period_effective_date &&
      policy_period_effective_date >= "2026-07-01";

    switch (policyFormType) {
      case "automobile_on": {
        if (useV2) {
          dispatch(
            previewAutomobilePDFApplicationsV2(
              customerPolicyId,
              incremental_number,
              () => {},
            ),
          );
        } else {
          dispatch(
            previewAutomobilePDFApplications(
              customerPolicyId,
              incremental_number,
              () => {},
            ),
          );
        }
        return;
      }
      case "automobile_ab": {
        dispatch(
          previewAutomobileAlbertaPDFApplications(
            customerPolicyId,
            incremental_number,
            () => {},
          ),
        );
        return;
      }
    }
  };

  const handlePreviewClickV2 = (
    policyFormType: string,
    incremental_number: number,
  ) => {
    switch (policyFormType) {
      case "automobile_on": {
        dispatch(
          previewAutomobilePDFApplicationsV2(
            customerPolicyId,
            incremental_number,
            () => {},
          ),
        );
      }
    }
  };

  const handleFormTypeClick = (value: string, incrementNumber?: any) => () => {
    setAnchorEl(null);
    let additionalparms = "";
    if (incrementNumber) {
      additionalparms = "/" + incrementNumber;
    }

    switch (value) {
      case "automobile_ab": {
        navigate(
          `/automobile-policy/${customerId}/automobile-application-tab/${customerPolicyId}/alberta-application${additionalparms}`,
        );
        return;
      }
      case "automobile_on": {
        navigate(
          `/automobile-policy/${customerId}/automobile-application-tab/${customerPolicyId}/application${additionalparms}`,
        );

        return;
      }
    }
  };

  const {
    state: { dateState, searchState, tabs, columnsConfig, dense },
    setDateState,
    setSearchState,
    setSelectedTab,
    setColumnVisibility,

    onDenseToggle,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last90Days",
      selectedTab: "-1",
    },
  });

  const fetchList = () => {
    dispatch(
      fetchAutomobileAllApplicationsListAsync(
        customerPolicyId,
        policyFormRoot,
        tablePagination.pageNumber,
        tablePagination.rowsInPerPage,
        status,
      ),
    );
  };

  const handleComplete = () => {
    fetchList();
    setOpenFile(false);
  };

  const handlequickApplication = () => {
    fetchList();
    setOpenView(null);
  };

  React.useEffect(() => {
    if (customerPolicyId) {
      fetchList();
    }
  }, [customerPolicyId, status]);

  const AutomobileApplicationDataProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    extraFetchFactor: tablePagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "none",
    uniqueRowKeyName: "incemental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                sx={{ minWidth: "120px" }}
                value={policyFormRoot}
                options={APPLICATION_TYPES}
                onChange={(e) => setPolicyFormRoot(e.target.value as string)}
              ></ControlledCustomSelect>
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS}
              />
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IAutomobileApplicationList) => {
          return (
            <StandardTableActions
              downloadLoading={downloading}
              onEditClick={handleFormTypeClick(
                row.application_type,
                row.incremental_number,
              )}
              // onDownLoadClick={handleDownloadPreview(
              //   row.incremental_number,
              //   "download"
              // )}
              onDownLoadClick={
                DOWNLOAD_FOR.includes(row.application_type)
                  ? () =>
                      handleDownloadClick(
                        row.application_type,
                        row.incremental_number,
                        row.policy_period_effective_date,
                      )
                  : undefined
              }
              onDownloadPreview={
                PREVIEW_FOR.includes(row.application_type)
                  ? () =>
                      handlePreviewClick(
                        row.application_type,
                        row.incremental_number as any,
                        row.policy_period_effective_date,
                      )
                  : undefined
              }
              otherActions={[
                ...(PREVIEW_FOR.includes(row.application_type)
                  ? [
                      {
                        icon: Preview,
                        tooltip: "Preview 2026",
                        onClick: () => {
                          handlePreviewClickV2(
                            row.application_type,
                            row.incremental_number as any,
                          );
                        },
                      },
                    ]
                  : []),
                {
                  icon: HistoryEdu,
                  tooltip: "G4Sign",
                  onClick: () => {
                    setG4SignApp(row);
                  },
                },
              ]}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IAutomobileApplicationList) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_period_effective_date",
        headerName: "Effective Date",
        fieldName: "policy_period_effective_date",
        renderType: DataTableV2RowRenderType.DATE,
      },
      {
        key: "application_type",
        headerName: "Application Type",
        fieldName: "application_type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 30,
        onRowCellValueRender: (value, row) => {
          return formatText(value).toUpperCase();
        },
      },
      {
        key: "type",
        headerName: "Type",
        fieldName: "type",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
    ],
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "create_ts",
      },
    },
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
    dense: dense,
    onDenseToggle: onDenseToggle,
  };

  return (
    <PageContainer title="Application" description="this is inner page">
      <PageLoader
        loading={customerInfo.loading === LoadState.InProgress}
        error={customerInfo.error ? { message: customerInfo.error } : null}
      >
        <PageTitleBar
          heading="Automobile Applications"
          rightHeading={
            <Stack direction={"row"} spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenView({
                    ...INITIAL_QUICK_APPLICATION,
                  });
                }}
              >
                Quick Create
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() =>
                  navigate(
                    `/automobile-policy/${customerId}/automobile-application-tab/${customerPolicyId}/quote-ars`,
                  )
                }
                endIcon={<RequestQuote />}
              >
                Create From ARS Quote
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setOpenQuotes(true)}
                endIcon={<RequestQuote />}
              >
                View Quotes
              </Button>
              <Button
                id="demo-customized-button"
                aria-controls={open ? "demo-customized-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="contained"
                disableElevation
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
              >
                Create
              </Button>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
              >
                {APPLICATION_TYPES.slice(1).map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={handleFormTypeClick(item.value)}
                      disableRipple
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </StyledMenu>
            </Stack>
          }
        />

        <DataTableV2 {...AutomobileApplicationDataProps} />
        {/* <TabsList
        sx={{ mb: 1 }}
        varaint="fullWidth"
        selectedTab={selectedTab}
        tabs={[
          {
            label: "Automobile (AB)",
            icon: <AdminPanelSettings />,
          },
          {
            label: "Automobile (ON)",
            icon: <AdminPanelSettings />,
          },
        ]}
        onChange={handleChange}
      />
      <TabPanel value={selectedTab} index={0}>
      <AutomobileAlbertaApplicationList />
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
      <AutomobileApplicationList />
      </TabPanel>
      <TabPanel value={selectedTab} index={2}>
      <AutomobileSupplementaryFormList
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      />
      </TabPanel>
      <TabPanel value={selectedTab} index={3}>
      <CommAutoInsuranceGarageApplicationList />
      </TabPanel>
      <TabPanel value={selectedTab} index={4}>
      <CommAutoInsuranceGarageApplicationList />
      </TabPanel> */}

        {openView && (
          <QuickApplicationDialog
            open={true}
            customerPolicyId={customerPolicyId}
            customerId={customerId}
            type={"automobile"}
            data={openView}
            onClose={() => setOpenView(null)}
            onComplete={handlequickApplication}
          />
        )}

        {g4SignApp && (
          <ApplicationG4SignDialog
            open={true}
            applicationNumber={g4SignApp.incremental_number}
            applicationType={g4SignApp.application_type}
            customerPolicyId={customerPolicyId}
            customerId={customerId}
            onClose={() => setG4SignApp(null)}
          />
        )}

        {openQuotes && (
          <PolicyQuotesRightPanel
            open={openQuotes}
            customerPolicyId={customerPolicyId}
            onClose={() => setOpenQuotes(false)}
          />
        )}
      </PageLoader>
    </PageContainer>
  );
};
